import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ChartLine } from '@phosphor-icons/react';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center noise-bg" style={{ background: '#0A0B0E' }}>
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <ChartLine size={40} weight="duotone" className="text-[#2563EB]" />
            <h1 className="text-3xl font-heading font-black tracking-tighter text-white">QuantoAura</h1>
          </div>
          <p className="text-sm text-[#94A3B8]">AI-Driven Trading Intelligence Platform</p>
        </div>

        <div className="bg-[#12141A] border border-white/10 p-6" data-testid="login-form">
          <h2 className="text-xl font-heading font-bold tracking-tight mb-6 text-white">Sign In</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-[#FF3366]/10 border border-[#FF3366]/30 text-[#FF3366] text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 bg-[#0A0B0E] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                placeholder="your@email.com"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 bg-[#0A0B0E] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2563EB] hover:bg-blue-600 text-white py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-[#94A3B8]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#2563EB] hover:text-blue-400 transition-colors" data-testid="register-link">
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}